import React, { useState, useEffect, useMemo } from 'react';
import { Search, SortAsc, SortDesc, Download, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../../auth/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const TeachersList = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { t } = useTranslation('teacherslist');
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // States for filtering and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/v1/teachers/schools/${user.school_reg}/teachers`);
        setTeachers(response.data);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTeachers();
  }, [api, user.school_reg]);

  useEffect(() => {
    // Reset selections when page changes
    setSelectedTeachers([]);
    setSelectAll(false);
  }, [currentPage]);

  const handleRowClick = (teacherId, e) => {
    // Prevent navigation if the click was on a checkbox
    if (e.target.type === 'checkbox' || e.target.closest('.checkbox-cell')) {
      return;
    }
    navigate(`/teacher-profile/${teacherId}`);
  };

  const handleCheckboxChange = (teacherId) => {
    setSelectedTeachers(prev => {
      if (prev.includes(teacherId)) {
        return prev.filter(id => id !== teacherId);
      } else {
        return [...prev, teacherId];
      }
    });
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedTeachers([]);
    } else {
      setSelectedTeachers(paginatedTeachers.map(teacher => teacher.id));
    }
    setSelectAll(!selectAll);
  };

  const handleDeleteSelected = async () => {
    if (selectedTeachers.length === 0) return;
    
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      // Use Promise.all to handle multiple delete requests concurrently
      await Promise.all(
        selectedTeachers.map(teacherId => 
          api.delete(`/api/v1/teachers/schools/${user.school_reg}/teachers/${teacherId}`)
        )
      );
      
      // Remove deleted teachers from state
      setTeachers(prev => prev.filter(teacher => !selectedTeachers.includes(teacher.id)));
      setSelectedTeachers([]);
      setSelectAll(false);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError(`${t('failedToDelete')}: ${err.message}`);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const getRandomColor = (name) => {
    const colors = [
      '#2563eb', '#7c3aed', '#db2777', '#dc2626', '#059669',
      '#ca8a04', '#0891b2', '#4f46e5', '#be123c', '#15803d'
    ];
    const index = name.split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  // Filter and sort teachers
  const filteredAndSortedTeachers = useMemo(() => {
    return teachers
      .filter(teacher => 
        (teacher.name && teacher.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (teacher.email && teacher.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (teacher.tsc_number && String(teacher.tsc_number).toLowerCase().includes(searchTerm.toLowerCase())) ||
        (teacher.id_number && String(teacher.id_number).toLowerCase().includes(searchTerm.toLowerCase()))
      )
      .sort((a, b) => {
        if (sortDirection === 'asc') {
          return a[sortField] > b[sortField] ? 1 : -1;
        }
        return a[sortField] < b[sortField] ? 1 : -1;
      });
  }, [teachers, searchTerm, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedTeachers.length / pageSize);
  const paginatedTeachers = filteredAndSortedTeachers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const exportToCSV = () => {
    const headers = [
      t('name'), 
      t('gender'), 
      t('email'), 
      t('phone'), 
      t('idNumber'), 
      t('tscNumber'), 
      t('dateOfJoin'), 
      t('dateOfBirth'), 
      t('address')
    ];
    
    const csvContent = [
      headers.join(','),
      ...filteredAndSortedTeachers.map(teacher => 
        [
          teacher.name,
          teacher.gender,
          teacher.email,
          teacher.phone,
          teacher.id_number,
          teacher.tsc_number,
          teacher.date_of_joining,
          teacher.date_of_birth,
          teacher.address
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'teachers.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const renderSortIcon = (field) => {
    if (sortField === field) {
      return sortDirection === 'asc' ? 
        <SortAsc size={16} className="inline ml-1" /> : 
        <SortDesc size={16} className="inline ml-1" />;
    }
    return null;
  };

  const sortableHeaderClasses = "bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 cursor-pointer select-none whitespace-nowrap hover:bg-slate-100";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {t('errorLoadingTeachers')}: {error}
      </div>
    );
  }

  return (
    <div className="rounded-xl">
      <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
        <div className="flex items-center bg-white border border-slate-200 rounded-lg px-4 py-2 w-full sm:w-auto sm:min-w-[300px]">
          <Search className="text-slate-500" size={20} />
          <input
            type="text"
            placeholder={t('searchTeachers')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-none outline-none ml-2 text-sm w-full"
          />
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-4 ml-auto">
          {selectedTeachers.length > 0 && (
            <button 
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200"
              onClick={handleDeleteSelected}
            >
              <Trash2 size={20} />
              {t('delete')} ({selectedTeachers.length})
            </button>
          )}
          <button 
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200"
            onClick={exportToCSV}
          >
            <Download size={20} />
            {t('exportCSV')}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg border border-slate-200 shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="bg-slate-50 p-3 border-b-2 border-slate-200 whitespace-nowrap w-8">
                <div className="flex items-center justify-center checkbox-cell">
                  <input 
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAllChange}
                    className="form-checkbox w-4 h-4 text-blue-500 rounded border-slate-300 cursor-pointer"
                  />
                </div>
              </th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">{t('photo')}</th>
              <th 
                className={sortableHeaderClasses}
                onClick={() => {
                  setSortField('name');
                  setSortDirection(sortField === 'name' ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc');
                }}
              >
                {t('name')} {renderSortIcon('name')}
              </th>
              <th 
                className={sortableHeaderClasses}
                onClick={() => {
                  setSortField('id_number');
                  setSortDirection(sortField === 'id_number' ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc');
                }}
              >
                {t('idNumber')} {renderSortIcon('id_number')}
              </th>
              <th 
                className={sortableHeaderClasses}
                onClick={() => {
                  setSortField('tsc_number');
                  setSortDirection(sortField === 'tsc_number' ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc');
                }}
              >
                {t('tscNumber')} {renderSortIcon('tsc_number')}
              </th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">{t('email')}</th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">{t('phone')}</th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">{t('gender')}</th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">{t('dateOfJoin')}</th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">{t('dateOfBirth')}</th>
              <th className="bg-slate-50 p-3 text-left font-semibold text-slate-600 border-b-2 border-slate-200 whitespace-nowrap">{t('address')}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTeachers.map(teacher => (
              <tr 
                key={teacher.id}
                onClick={(e) => handleRowClick(teacher.id, e)}
                className={`cursor-pointer hover:bg-slate-50 ${
                  selectedTeachers.includes(teacher.id) ? 'bg-blue-50' : ''
                }`}
              >
                <td className="p-3 border-b border-slate-200 w-8">
                  <div 
                    className="flex items-center justify-center checkbox-cell"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input 
                      type="checkbox"
                      checked={selectedTeachers.includes(teacher.id)}
                      onChange={() => handleCheckboxChange(teacher.id)}
                      className="form-checkbox w-4 h-4 text-blue-500 rounded border-slate-300 cursor-pointer"
                    />
                  </div>
                </td>
                <td className="p-3 border-b border-slate-200">
                  {teacher.photo ? (
                    <img 
                      src={teacher.photo}
                      alt={teacher.name}
                      className="w-10 h-10 rounded-full object-cover bg-slate-200"
                    />
                  ) : (
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white uppercase"
                      style={{ backgroundColor: getRandomColor(teacher.name) }}
                    >
                      {teacher.name.charAt(0)}
                    </div>
                  )}
                </td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{teacher.name}</td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{teacher.id_number}</td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{teacher.tsc_number}</td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{teacher.email}</td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{teacher.phone}</td>
                <td className="p-3 border-b border-slate-200">
                  <span className={`px-2 py-1 rounded-xl text-xs font-medium ${
                    teacher.gender === 'MALE' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-pink-100 text-pink-800'
                  }`}>
                    {t(teacher.gender.toLowerCase())}
                  </span>
                </td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{teacher.date_of_joining ? format(new Date(teacher.date_of_joining), 'dd/MM/yyyy') : '-'}</td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{teacher.date_of_birth ? format(new Date(teacher.date_of_birth), 'dd/MM/yyyy') : '-'}</td>
                <td className="p-3 border-b border-slate-200 text-slate-700">{teacher.address}</td>
              </tr>
            ))}
            {paginatedTeachers.length === 0 && (
              <tr>
                <td colSpan="11" className="p-6 text-center text-slate-500">
                  {t('noTeachersFound')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Responsive pagination controls */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
        <div className="text-sm text-slate-500 order-2 sm:order-1">
          {t('totalTeachers')}: {filteredAndSortedTeachers.length}
        </div>
        
        <div className="flex items-center justify-center gap-4 order-1 sm:order-2">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-2 border border-slate-200 bg-white rounded-lg cursor-pointer flex items-center transition-all duration-200 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="text-slate-600 text-sm">
            {t('page')} {currentPage} {t('of')} {totalPages || 1}
          </span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages || totalPages === 0}
            className="p-2 border border-slate-200 bg-white rounded-lg cursor-pointer flex items-center transition-all duration-200 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">{t('confirmDelete')}</h3>
            <p className="mb-6">
              {t('deleteConfirmationMessage', { count: selectedTeachers.length })}
            </p>
            <div className="flex justify-end gap-3">
              <button 
                onClick={cancelDelete}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                {t('cancel')}
              </button>
              <button 
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                {t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeachersList;